import type { App } from '../app/App';
import { RULES, skillDef } from '../game/combat/rules';
import { heroClass } from '../game/data/presets';
import { heroArtId, heroDrawing, heroStats } from '../game/systems/hero';
import type { Hero, Monster } from '../game/systems/model';
import { monsterArt } from '../game/systems/monsters';
import { canShareOutside } from '../game/systems/policy';
import { ensureOnline } from '../networking/session';
import { t } from '../i18n';
import { button, modal, thumb } from './components';
import { ensureDrawingRules } from './drawingRules';
import { h } from './dom';

/**
 * Show-off pictures: the game draws a 1200×630 card (the size Facebook
 * previews use), uploads it, and shares the link /s/{id}, whose page carries
 * the card as its Open Graph picture. Not offered in kid mode.
 */

interface CardInfo {
  kind: 'hero' | 'monster';
  /** Big name on the card; also the link title. */
  title: string;
  /** One line under the name, e.g. "Level 12 · Pencil Knight". */
  badge: string;
  art: HTMLCanvasElement;
  stats: [string, number][];
  /** Smaller extra lines (wins, tower floor, fight code). */
  lines: string[];
}

const W = 1200;
const H = 630;

export function canShare(app: App): boolean {
  return canShareOutside(app.store.state);
}

export function shareHero(app: App, hero: Hero): Promise<void> {
  const save = app.store.state;
  const s = heroStats(hero, save.itemLevels);
  return openShare(app, {
    kind: 'hero',
    title: hero.name,
    badge: t('Level {level} · {class}', { level: hero.level, class: t(heroClass(hero.classId).name) }),
    art: thumb(app, heroDrawing(hero), heroArtId(hero), 220),
    stats: [['HP', s.maxHp], ['ATK', s.atk], ['DEF', s.def], ['LUCK', s.luck]],
    lines: [
      t('{n} wins · {beaten} story monsters beaten', { n: save.counters.wins, beaten: save.defeatedPresets.length }),
      save.counters.towerBest > 0 ? t('Homework Tower: floor {n}', { n: save.counters.towerBest }) : '',
    ].filter(Boolean),
  });
}

export function shareMonsterCard(app: App, m: Monster): Promise<void> {
  const tier = t(RULES.budget.tiers.find((x) => x.id === m.tier)?.name ?? m.tier);
  return openShare(app, {
    kind: 'monster',
    title: m.name,
    badge: `${tier} · ${t(skillDef(m.skill).name)}`,
    art: thumb(app, m.drawing, monsterArt(m) ?? 'monster:gum_blob', 220),
    stats: [['HP', m.hp], ['ATK', m.atk], ['DEF', m.def], ['LUCK', m.luck]],
    lines: [
      app.store.hero ? t('Drawn by {name}', { name: app.store.hero.name }) : '',
      m.shareCode ? t('Fight it in the game with code {code}', { code: m.shareCode }) : '',
    ].filter(Boolean),
  });
}

/* ---------- drawing the card ---------- */

function fitFont(ctx: CanvasRenderingContext2D, text: string, family: string, weight: string, start: number, maxWidth: number): void {
  let size = start;
  do {
    ctx.font = `${weight} ${size}px ${family}`;
    size -= 2;
  } while (ctx.measureText(text).width > maxWidth && size > 18);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, hh: number, r: number): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, hh, r);
}

async function drawCard(info: CardInfo): Promise<HTMLCanvasElement> {
  await document.fonts?.ready;
  const css = getComputedStyle(document.body);
  const body = css.getPropertyValue('--font-body').trim() || css.fontFamily;
  const title = css.getPropertyValue('--font-title').trim() || body;
  const c = h('canvas', { width: W, height: H }) as HTMLCanvasElement;
  const ctx = c.getContext('2d')!;
  // Notebook paper: ruled lines and a red margin.
  ctx.fillStyle = '#fdf8ec';
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = 'rgba(80, 140, 220, 0.25)';
  ctx.lineWidth = 2;
  for (let y = 60; y < H; y += 42) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
  }
  ctx.strokeStyle = 'rgba(220, 70, 70, 0.45)';
  ctx.beginPath();
  ctx.moveTo(84, 0);
  ctx.lineTo(84, H);
  ctx.stroke();

  // The doodle, taped on like a photo.
  ctx.save();
  ctx.translate(340, 315);
  ctx.rotate(-0.035);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
  roundRect(ctx, -222, -232, 452, 472, 14);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#3b3b3b';
  ctx.lineWidth = 4;
  roundRect(ctx, -230, -240, 452, 472, 14);
  ctx.fill();
  ctx.stroke();
  ctx.drawImage(info.art, -210, -220, 412, 412);
  ctx.fillStyle = 'rgba(246, 213, 92, 0.75)';
  ctx.fillRect(-60, -262, 120, 40);
  ctx.restore();

  // Words on the right.
  const x = 610;
  const maxW = W - x - 50;
  ctx.fillStyle = '#7a6f5f';
  ctx.font = `700 30px ${body}`;
  ctx.fillText(info.kind === 'hero' ? t('My hero') : t('A monster I drew'), x, 110);
  ctx.fillStyle = '#2b2b2b';
  fitFont(ctx, info.title, title, '700', 76, maxW);
  ctx.fillText(info.title, x, 190);
  ctx.fillStyle = '#4a4a4a';
  fitFont(ctx, info.badge, body, '700', 34, maxW);
  ctx.fillText(info.badge, x, 240);

  const colours = ['#ff8fab', '#f6b26b', '#6fa8dc', '#93c47d'];
  info.stats.forEach(([label, value], i) => {
    const cx = x + (i % 2) * 260;
    const cy = 280 + Math.floor(i / 2) * 84;
    ctx.fillStyle = colours[i % colours.length]!;
    ctx.strokeStyle = '#3b3b3b';
    ctx.lineWidth = 3;
    roundRect(ctx, cx, cy, 240, 66, 16);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#2b2b2b';
    ctx.font = `700 28px ${body}`;
    ctx.fillText(label, cx + 18, cy + 43);
    ctx.font = `700 38px ${body}`;
    const v = String(value);
    ctx.fillText(v, cx + 222 - ctx.measureText(v).width, cy + 46);
  });

  ctx.fillStyle = '#4a4a4a';
  info.lines.slice(0, 2).forEach((line, i) => {
    fitFont(ctx, line, body, '400', 28, maxW);
    ctx.fillText(line, x, 490 + i * 38);
  });

  // Footer: where to play.
  ctx.fillStyle = '#f6d55c';
  ctx.fillRect(x - 10, H - 66, maxW + 20, 46);
  ctx.fillStyle = '#2b2b2b';
  fitFont(ctx, `Paper Legends · ${location.host}`, title, '700', 30, maxW);
  ctx.fillText(`Paper Legends · ${location.host}`, x, H - 32);
  return c;
}

/* ---------- the dialog ---------- */

const toJpeg = (c: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('could not make the picture'))), 'image/jpeg', 0.86));

async function toBase64(b: Blob): Promise<string> {
  const bytes = new Uint8Array(await b.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

async function openShare(app: App, info: CardInfo): Promise<void> {
  if (!canShare(app) || !(await ensureDrawingRules(app))) return;
  const card = await drawCard(info);
  const jpeg = await toJpeg(card);
  const fileName = `paper-legends-${info.title.replace(/[^\p{L}\p{N}]+/gu, '-').slice(0, 30) || info.kind}.jpg`;
  let link: string | null = null;

  const status = h('p', { class: 'hand-hint share-status', 'aria-live': 'polite' }, t('Getting your link ready…'));
  const preview = h('img', { class: 'share-preview', src: URL.createObjectURL(jpeg), alt: t('Your show-off picture') });
  const need = (): boolean => {
    if (link) return true;
    app.toast(t('The link is not ready yet.'), 'info');
    return false;
  };
  const facebook = button(t('Share on Facebook'), {
    variant: 'primary',
    color: 'blue',
    sfx: app,
    onClick: () => need() && window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link!)}`, 'fb-share', 'width=640,height=600'),
  });
  const native =
    typeof navigator.share === 'function'
      ? button(t('Share to an app…'), {
          color: 'green',
          sfx: app,
          onClick: () => need() && void navigator.share({ title: `${info.title} — Paper Legends`, url: link! }).catch(() => {}),
        })
      : null;
  const copy = button(t('Copy link'), {
    small: true,
    sfx: app,
    onClick: () => need() && void navigator.clipboard.writeText(link!).then(() => app.toast(t('Link copied!'), 'success'), () => app.toast(link!, 'info')),
  });
  const save = button(t('Save picture'), {
    small: true,
    sfx: app,
    onClick: () => {
      const a = h('a', { href: preview.src, download: fileName }) as HTMLAnchorElement;
      a.click();
    },
  });
  const body = h('div', { class: 'share-dialog' }, preview, status, h('div', { class: 'share-actions' }, facebook, native, copy, save));
  const shown = modal(t('Show it off!'), body, [{ label: t('Close'), value: 'close', variant: 'ghost' }], { className: 'share-card' });

  try {
    if (!(await ensureOnline(app, false))) throw new Error(t('Cannot reach the game server. You can still save the picture.'));
    const res = await app.api.raw<{ id: string; url: string }>('POST', '/api/share-cards', {
      kind: info.kind,
      title: info.title.slice(0, 40),
      subtitle: [info.badge, ...info.lines].join(' · ').slice(0, 120),
      image: await toBase64(jpeg),
    });
    link = res.url;
    status.textContent = t('Ready! Your friends will see this picture with a link to the game.');
  } catch (err) {
    status.textContent = err instanceof Error ? err.message : t('Could not make the link. You can still save the picture.');
  }
  await shown;
  URL.revokeObjectURL(preview.src);
}
